import { isEqual } from 'lodash'

import type { AppRouter } from '@/server/routers/_app'
import queryClient from '@/services/queryClient'

import {
  scheduleInvalidateQueries,
  scheduleInvalidation,
} from './realtime/coalescedInvalidation'

export type LastModification = {
  networkId: string | null
  network: string | null
  variableState: string | null
  assumptionSet: string | null
  assumption: string | null
  requirement: string | null
  history: string | null
  networks: Array<{ networkId: string; updateTime: string }>
  views: Array<{ viewId: string; updateTime: string }>
}

/**
 * Invalidates every cached instance of the paginated network browser query
 * (header popover, inline Home page browser, etc.), deciding per-instance
 * whether to force an active refetch.
 *
 * getNetworksWithLastEditorPage is paginated by a cursor derived from
 * last_modification -- exactly the field that changes whenever any network
 * is created/renamed/edited. A single loaded page can only gain rows at
 * the top, so refetching it live is safe and keeps the list current for
 * viewers who haven't scrolled. Once more than one page is loaded, an
 * active refetch replays the whole cursor chain from scratch and can
 * reshuffle page boundaries mid-scroll (KBE-670). Those are marked stale
 * instead -- they pick up fresh data next time they mount, or immediately
 * if the current tab is the one causing the change (see the explicit
 * refetches in NetworkPopoverContainer/useCreateNetwork/etc.).
 */
export function scheduleNetworksListInvalidationPreservingScroll(): void {
  // Deliberately keyed on the procedure rather than on the individual query
  // keys: the cache is enumerated when the window flushes, so one scheduled
  // pass covers every instance mounted by then, including any that appeared
  // during the burst.
  scheduleInvalidation('network.getNetworksWithLastEditorPage', () => {
    const queries = queryClient
      .getQueryCache()
      .findAll({ queryKey: [['network', 'getNetworksWithLastEditorPage']] })
    for (const query of queries) {
      const data = query.state.data as { pages?: unknown[] } | undefined
      const pageCount = data?.pages?.length ?? 0
      void queryClient.invalidateQueries(
        {
          queryKey: query.queryKey,
          exact: true,
          refetchType: pageCount > 1 ? 'none' : 'active',
        },
        { cancelRefetch: false },
      )
    }
  })
}

type RouterName = keyof AppRouter

type ProcedureName<TRouter extends RouterName> =
  keyof AppRouter[TRouter] extends string ? keyof AppRouter[TRouter] : never

/**
 * Normalizes timestamp strings to ISO 8601 format for consistent comparison.
 * Handles different formats from database and webhooks:
 * - "2025-12-08 10:21:47.951644+00" -> "2025-12-08T10:21:47.951644+00:00"
 * - "2025-12-08T13:57:39.111Z" -> stays as is
 */
function normalizeTimestamp(
  timestamp: string | null | undefined,
): string | null {
  if (!timestamp) return null
  try {
    return new Date(timestamp).toISOString()
  } catch {
    return timestamp
  }
}

/**
 * Invalidates downstream tRPC queries based on timestamp changes.
 * Can be called from anywhere (not just React components) since it uses queryClient directly.
 *
 * @param networkId - The network ID for network-specific invalidations
 * @param newTimestamps - The new timestamp data
 * @param previousTimestamps - Optional previous data (if not provided, reads from cache)
 * @param changedViewId - Optional viewId for view-specific invalidation (avoids invalidating all views)
 */
export function invalidateQueriesFromTimestamps(
  networkId: string | undefined,
  newTimestamps: LastModification,
  previousTimestamps?: LastModification | null,
  changedViewId?: string,
): void {
  const previousData =
    previousTimestamps ??
    queryClient.getQueryData<LastModification>(['lastModification', networkId])

  // Type-safe helper to invalidate a tRPC query by router and procedure name.
  // Scheduled rather than immediate so a burst of broadcasts costs one refetch
  // per procedure instead of one per broadcast (KBE-806).
  const invalidate = <TRouter extends RouterName>(
    router: TRouter,
    procedure: ProcedureName<TRouter>,
  ) => {
    scheduleInvalidateQueries([[router, procedure]])
  }

  if (!isEqual(newTimestamps.networks, previousData?.networks)) {
    invalidate('network', 'getNetworksOrderedByLastModification')
    scheduleNetworksListInvalidationPreservingScroll()
    invalidate('network', 'listNetworkNamesForLists')
    invalidate('network', 'listDistinctNetworkEditors')
    invalidate('variableLineage', 'getLineage')
  }

  if (networkId) {
    if (
      normalizeTimestamp(newTimestamps.network) !==
      normalizeTimestamp(previousData?.network)
    ) {
      invalidate('network', 'getNetwork')
      invalidate('network', 'getNetworkWithVariableStatesAndFaults')
      invalidate('assumptionSet', 'getNetworkWithAssumptionSets')
      invalidate('variableLineage', 'getLineage')
      // KBE-853: the Branch changes view composes a delta branch against its
      // live base, so a base edit changes it too. Procedure-wide (no input key),
      // so it reaches an open branch even though this broadcast is the base's.
      invalidate('network', 'getDeltaBranchChanges')
    }

    if (
      normalizeTimestamp(newTimestamps.variableState) !==
      normalizeTimestamp(previousData?.variableState)
    ) {
      invalidate('network', 'getNetworkVariableStates')
      invalidate('assumptionSet', 'getAllAssumptionSetRelatedData')
      invalidate('network', 'getNetworkWithVariableStatesAndFaults')
      invalidate('assumptionSet', 'getAssumptionSets')
      invalidate('assumptionSet', 'getAssumptionSetAggregates')
      invalidate('assumptionSet', 'getAssumptions')
      invalidate('variableLineage', 'getLineage')
      invalidate('network', 'getDeltaBranchChanges')
    }

    if (
      normalizeTimestamp(newTimestamps.history) !==
      normalizeTimestamp(previousData?.history)
    ) {
      invalidate('history', 'searchHistory')
    }

    if (!isEqual(newTimestamps.views, previousData?.views)) {
      invalidate('view', 'getViews')

      if (changedViewId) {
        // Keyed on the viewId by `scheduleInvalidateQueries`, so two different
        // views changing inside one window both still get invalidated.
        scheduleInvalidateQueries([
          ['view', 'getViewWithPositions'],
          { input: { viewId: changedViewId } },
        ])
      } else {
        invalidate('view', 'getViewWithPositions')
      }
    }

    if (
      normalizeTimestamp(newTimestamps.assumptionSet) !==
      normalizeTimestamp(previousData?.assumptionSet)
    ) {
      invalidate('assumptionSet', 'getAssumptionSets')
      invalidate('assumptionSet', 'getAllAssumptionSetRelatedData')
      invalidate('assumptionSet', 'getAssumptionSetAggregates')
      invalidate('assumptionSet', 'getNetworkWithAssumptionSets')
      // KBE-853: the Branch changes view also lists assumption-set / requirement
      // / assumption changes, which bump these buckets (not network/variableState).
      invalidate('network', 'getDeltaBranchChanges')
    }

    if (
      normalizeTimestamp(newTimestamps.requirement) !==
      normalizeTimestamp(previousData?.requirement)
    ) {
      invalidate('assumptionSet', 'getRequirements')
      invalidate('assumptionSet', 'getAllAssumptionSetRelatedData')
      invalidate('assumptionSet', 'getAssumptionSetAggregates')
      invalidate('assumptionSet', 'getNetworkWithAssumptionSets')
      invalidate('network', 'getDeltaBranchChanges')
    }

    if (
      normalizeTimestamp(newTimestamps.assumption) !==
      normalizeTimestamp(previousData?.assumption)
    ) {
      invalidate('assumptionSet', 'getAssumptions')
      invalidate('assumptionSet', 'getAllAssumptionSetRelatedData')
      invalidate('assumptionSet', 'getAssumptionSetAggregates')
      invalidate('assumptionSet', 'getAssumptionSets')
      invalidate('assumptionSet', 'getNetworkWithAssumptionSets')
      invalidate('network', 'getDeltaBranchChanges')
    }
  }
}
